<script lang="ts">
    import { onDestroy, onMount } from "svelte";
    import { fade } from "svelte/transition";
    import ajax from "../ajax/ajax";
    import Paper from "../components/paper/Paper.svelte";
    import Button from "../components/buttons/Button.svelte";
    import Alert from "../components/alert/Alert.svelte";
    import TextInput from "../components/form/input/TextInput.svelte";
    import { link } from "svelte-spa-router";
    import { userStore } from "../auth/userStore";
    import type { User } from "../auth/userStore";
    import IconButton from "../components/buttons/IconButton.svelte";
    import DeleteIcon from "../components/icons/DeleteIcon.svelte";
    import Snackbar from "../components/snackbar/Snackbar.svelte";
    import SettingsIcon from "../components/icons/SettingsIcon.svelte";
    import { push } from "svelte-spa-router";
    import MainMenu from "../components/menu/MainMenu.svelte";

    let user: User;
    const unsubscribeUser = userStore.subscribe((v) => {
        user = v;
    });
    onDestroy(unsubscribeUser);

    let isLoading = true;

    type TProject = {
        name: string;
        repository: string;
        id: number;
        userId: number;
        changed: number;
    };

    let projects: TProject[];

    let newProjectName: string = "";
    let newProjectRepository: string = "";

    let createMessage: {
        type: "success" | "error";
        message: string;
    } | null = null;

    let snackbarTimout: number | null = null;
    let snackbarMessage: {
        type: "success" | "error";
        message: string;
    } | null = null;

    function getErrorFromAjaxReturn(
        statusText: string,
        status: number,
        xhr: XMLHttpRequest
    ): { type: "success" | "error"; message: string } {
        try {
            const returnValue = JSON.parse(xhr.responseText);
            if (returnValue) {
                return {
                    type: "error",
                    message: returnValue.errorMessage,
                };
            }
        } catch {}

        return {
            type: "error",
            message: statusText,
        };
    }

    const ajaxRequest = ajax.get(
        "/projects",
        (result: { projects: TProject[] }) => {
            projects = result.projects;
        },
        (statusText: string, status: number, xhr: XMLHttpRequest) => {
            snackbarMessage = getErrorFromAjaxReturn(statusText, status, xhr);
        }
    );
    onMount(() => {
        return () => {
            ajaxRequest.cancel();
        };
    });

    function createProject(name: string, repository: string) {
        ajax.put(
            "/projects",
            {
                name,
                repository,
            },
            (result: {
                ack: boolean;
                project: TProject | null;
                message?: string;
            }) => {
                if (result.ack) {
                    if (result.project) {
                        createMessage = null;
                        if (snackbarTimout) {
                            clearTimeout(snackbarTimout);
                        }
                        snackbarMessage = {
                            type: "success",
                            message: `Project "${result.project.name}" created.`,
                        };
                        snackbarTimout = setTimeout(() => {
                            snackbarMessage = null;
                        }, 5000);
                        newProjectName = "";
                        newProjectRepository = "";
                        projects.unshift({
                            id: result.project.id,
                            name: result.project.name,
                            repository: result.project.repository,
                            userId: result.project.userId,
                            changed: result.project.changed,
                        });
                        projects = projects;
                    } else {
                        createMessage = {
                            type: "error",
                            message: "Project was not created.",
                        };
                    }
                } else {
                    createMessage = { type: "error", message: result.message };
                }
            },
            (statusText: string, status: number, xhr: XMLHttpRequest) => {
                createMessage = getErrorFromAjaxReturn(statusText, status, xhr);
            }
        );
    }

    let version = null;
    ajax.get(
        "/version",
        (result: { version: string }) => {
            version = result.version;
        },
        (statusText: string, status: number, xhr: XMLHttpRequest) => {
            // EAT
        }
    );
</script>

<MainMenu />
<div class="ProjectPage">
    <Paper>
        <h1>Projects</h1>
        {#if projects && projects.length > 0}
            <div class="ProjectList">
                <table class="table table-striped">
                    <tbody>
                        {#each projects as project (project.id)}
                            <tr class="Project">
                                <td>
                                    <a href="/project/{project.id}" use:link
                                        >{project.name}</a
                                    >
                                </td>
                                <td class="actions">
                                    {#if project.userId === user.id}
                                        <IconButton
                                            icon={DeleteIcon}
                                            title="Delete"
                                            onClick={() => {
                                                if (
                                                    confirm(
                                                        `Delete project ${project.name}?`
                                                    )
                                                ) {
                                                    ajax.delete(
                                                        `/projects/${project.id}`,
                                                        () => {
                                                            if (
                                                                snackbarTimout
                                                            ) {
                                                                clearTimeout(
                                                                    snackbarTimout
                                                                );
                                                            }
                                                            snackbarMessage = {
                                                                type: "success",
                                                                message: `Project ${project.name} deleted.`,
                                                            };
                                                            snackbarTimout = setTimeout(
                                                                () => {
                                                                    snackbarMessage = null;
                                                                },
                                                                5000
                                                            );
                                                            const indexToDelete = projects.findIndex(
                                                                (candidate) => {
                                                                    return (
                                                                        candidate.id ===
                                                                        project.id
                                                                    );
                                                                }
                                                            );
                                                            projects.splice(
                                                                indexToDelete,
                                                                1
                                                            );
                                                            projects = projects;
                                                        },
                                                        (
                                                            statusText,
                                                            status,
                                                            xhr
                                                        ) => {
                                                            snackbarMessage = getErrorFromAjaxReturn(
                                                                statusText,
                                                                status,
                                                                xhr
                                                            );
                                                        }
                                                    );
                                                }
                                            }}
                                        />
                                        <IconButton
                                            icon={SettingsIcon}
                                            title="Settings"
                                            onClick={() => {
                                                push(
                                                    `/project/${project.id}/settings`
                                                );
                                            }}
                                        />
                                    {/if}
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {:else}
            <div class="NoProjects">
                Currently no projects. Create the first one!
            </div>
        {/if}
    </Paper>

    <Paper>
        <h1>Add new project</h1>
        <form
            on:submit|preventDefault={() => {
                createProject(newProjectName, newProjectRepository);
            }}
        >
            <TextInput
                type="text"
                value={newProjectName}
                label="Name"
                onchange={(value) => {
                    newProjectName = value;
                }}
            />
            <TextInput
                type="text"
                value={newProjectRepository}
                label="Repository"
                onchange={(value) => {
                    newProjectRepository = value;
                }}
            />
            <Button
                type="submit"
                label="Add project"
                color="primary"
                elevation="elevation-1"
            />
        </form>
        {#if createMessage}
            <Alert type={createMessage.type} message={createMessage.message} />
        {/if}
    </Paper>
    {#if version}Version: {version}, Frontend: 1.2.4{/if}
</div>

{#if snackbarMessage}
    <div in:fade={{ duration: 300 }} out:fade>
        <Snackbar
            message={snackbarMessage.message}
            type={snackbarMessage.type}
        />
    </div>
{/if}

<style type="text/scss">
    h1 {
        font-size: 1.5rem;
        line-height: 1.5rem;
        margin: 0 0 1.5rem 0;
    }

    .ProjectPage {
        padding: 50px;
        :global(.form-item) {
            margin-bottom: 20px;
        }
    }

    .Project a {
        display: block;
        color: #333;
        font-size: 22px;
    }
</style>
